import React, { useState } from 'react';
import { apiFetch } from '../utils/api';
import './BulkExport.css';

function BulkExport({ org }) {
  const [file, setFile] = useState(null);
  const [exportType, setExportType] = useState('t4');
  const [exportFormat, setExportFormat] = useState('zip');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setError('');
      setSuccess('');
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
      setError('');
      setSuccess('');
    }
  };

  const handleExport = async (e) => {
    e.preventDefault();
    if (!org?.id) {
      setError('No organization selected. Please select one from the header first.');
      return;
    }
    if (!file) {
      setError('Please select an Excel or CSV data file to upload.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', year);
    formData.append('format', exportFormat);

    try {
      const response = await apiFetch(`/organizations/${org.id}/${exportType}/bulk-zip/upload`, org.tenantId, {
        method: 'POST',
        body: formData,
        parseResponse: false, // We expect a file blob, not JSON
      });

      if (!response.ok) {
        let errMessage = 'Failed to process bulk export logic.';
        try {
          const errData = await response.json();
          errMessage = errData.error || errMessage;
        } catch (parseErr) {}
        throw new Error(errMessage);
      }

      // Trigger the file download seamlessly
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;

      const disposition = response.headers.get('content-disposition');
      let filename = `${org.name.replace(/ /g, '_')}_${exportType.toUpperCase()}_Bulk_Export_${year}.zip`;
      if (disposition && disposition.indexOf('filename=') !== -1) {
          const matches = /filename="?([^"]+)"?/.exec(disposition);
          if (matches != null && matches[1]) filename = matches[1];
      }

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess('Bulk Export ZIP file generated and downloaded successfully!');
      setFile(null); // Reset after success
      const fileInput = document.getElementById('bulk-file-upload');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="op-bulk container-fluid py-4 px-md-4" style={{ minHeight: '100vh' }}>

      <div className="mb-4">
        <h2 className="h4 op-page-title mb-1">Bulk Export Generator</h2>
        <p className="op-subtitle mb-0">Upload Excel data to generate XML, PDFs, or both grouped in a ZIP package.</p>
      </div>

      <div className="card op-card shadow-sm border-0 rounded-3" style={{ maxWidth: '600px' }}>
        <div className="card-body p-4">
          {error && <div className="alert op-alert-danger p-3 mb-4 rounded-3">{error}</div>}
          {success && <div className="alert op-alert-success p-3 mb-4 rounded-3">{success}</div>}

          <form onSubmit={handleExport} className="d-flex flex-column gap-4">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label small fw-bold op-label text-uppercase">Export Type</label>
                <select value={exportType} onChange={(e) => {
                  setExportType(e.target.value);
                  if (e.target.value === 't2202' && exportFormat === 'pdf') {
                    setExportFormat('zip');
                  }
                }} className="form-select op-field shadow-none">
                  <option value="t4">T4 Slips</option>
                  <option value="t4a">T4A Slips</option>
                  <option value="t2202">T2202 Forms</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold op-label text-uppercase">Format</label>
                <select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} className="form-select op-field shadow-none">
                  <option value="zip">Both (ZIP)</option>
                  <option value="xml">XML Only</option>
                  {exportType !== 't2202' && <option value="pdf">PDF Only</option>}
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label small fw-bold op-label text-uppercase">Tax Year</label>
                <input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="form-control op-field shadow-none" min="2000" max="2099" />
              </div>
            </div>
            <div>
              <label className="form-label small fw-bold op-label text-uppercase">Data File (.xlsx or .csv)</label>
              <div
                className={`p-5 text-center rounded-3 op-dropzone ${dragActive ? 'op-dropzone-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                style={{ cursor: 'pointer' }}
                onClick={() => document.getElementById('bulk-file-upload').click()}
              >
                <input id="bulk-file-upload" type="file" accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" onChange={handleFileChange} className="d-none" />
                {file ? (
                  <div className="op-dropzone-filename fw-bold">Selected file: {file.name}</div>
                ) : (
                  <div className="op-dropzone-hint">Drag and drop your Excel/CSV file here, or click to browse</div>
                )}
              </div>
              <p className="mt-2 op-help-text small">Ensure the first row contains the exact column headers required (e.g. first_name, last_name, sin, etc.).</p>
            </div>
            <button type="submit" disabled={loading} className="btn op-btn-primary fw-bold py-3 w-100">
              {loading ? <span><span className="spinner-border spinner-border-sm op-spinner-border me-2"></span>Processing...</span> : 'Upload & Generate ZIP'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default BulkExport;